import { useLang } from "../../lib/LanguageContext";
import { IMAGES } from "../../lib/brand";
import { Package, Calculator, Megaphone } from "lucide-react";
import MarginCalculator from "./MarginCalculator";

export default function Features() {
  const { t } = useLang();
  const items = [
    {
      icon: Package,
      title: t.features.f1Title,
      body: t.features.f1Body,
      img: IMAGES.factory,
      tone: "red",
    },
    {
      icon: Calculator,
      title: t.features.f2Title,
      body: t.features.f2Body,
      tone: "orange",
    },
    {
      icon: Megaphone,
      title: t.features.f3Title,
      body: t.features.f3Body,
      img: IMAGES.marketingKit,
      tone: "ink",
    },
  ];

  return (
    <section id="features" className="py-20 sm:py-28" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-red">{t.features.eyebrow}</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight" data-testid="features-title">
            {t.features.title}
          </h2>
        </div>

        {/* Bento grid */}
        <div className="mt-14 grid lg:grid-cols-12 gap-5 lg:gap-6">
          {/* Feature 1 — unique sourcing */}
          <div className="lg:col-span-7 rounded-3xl bg-white border border-soft p-8 sm:p-10 soft-shadow hover:-translate-y-1 transition-transform duration-300 overflow-hidden relative" data-testid="feature-card-1">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-brand-red/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-brand-red" />
              </div>
              <div>
                <h3 className="font-display font-bold text-2xl text-ink">{items[0].title}</h3>
              </div>
            </div>
            <p className="text-ink-muted leading-relaxed max-w-lg">{items[0].body}</p>
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img src={items[0].img} alt={items[0].title} className="w-full h-56 object-cover" />
            </div>
          </div>

          {/* Feature 2 — margin calc (interactive) */}
          <div className="lg:col-span-5 rounded-3xl bg-ink text-white p-8 sm:p-10 soft-shadow relative overflow-hidden" data-testid="feature-card-2">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-orange/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-brand-orange/20 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-brand-orange" />
                </div>
                <h3 className="font-display font-bold text-2xl">{items[1].title}</h3>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-6">{items[1].body}</p>
              <MarginCalculator />
            </div>
          </div>

          {/* Feature 3 — marketing kit */}
          <div className="lg:col-span-12 rounded-3xl bg-gradient-to-br from-[#FEECEE] via-white to-[#FFF4E6] border border-soft p-8 sm:p-10 soft-shadow hover:-translate-y-1 transition-transform duration-300 overflow-hidden relative" data-testid="feature-card-3">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center soft-shadow">
                    <Megaphone className="w-6 h-6 text-brand-red" />
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl text-ink leading-tight">{items[2].title}</h3>
                </div>
                <p className="text-ink-muted leading-relaxed">{items[2].body}</p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {['Video', 'Banner', 'Photo', 'Copy', 'Reels', 'WhatsApp'].map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full bg-white border border-soft text-xs font-semibold text-ink">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border-4 border-white soft-shadow">
                <img src={items[2].img} alt="Marketing kit" className="w-full h-64 lg:h-80 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
